import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleReset = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setError("");
    setLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Reset failed");
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--surface-cream)" }}>
        <div className="buddy-card p-8 text-center max-w-md">
          <h2 className="font-display text-xl font-semibold" style={{ color: "var(--error)" }}>Invalid Reset Link</h2>
          <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>This password reset link is invalid or has expired.</p>
          <button onClick={() => navigate("/login")} className="btn-primary py-2 px-6 mt-4 text-sm">Back to Login</button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--surface-cream)" }}>
        <div className="buddy-card p-8 text-center max-w-md">
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "var(--success-bg)" }}>
            <Check size={28} style={{ color: "var(--success)" }} />
          </div>
          <h2 className="font-display text-xl font-semibold" style={{ color: "var(--text-primary)" }}>Password Reset!</h2>
          <p className="text-sm mt-2 mb-6" style={{ color: "var(--text-muted)" }}>Your password has been updated. Sign in with your new password.</p>
          <button onClick={() => navigate("/login")} className="btn-primary py-2.5 px-6 text-sm" data-testid="reset-login-btn">Sign In</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--surface-cream)" }}>
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-semibold" style={{ color: "var(--rolex-green)" }}>Buddy</h1>
          <h2 className="font-display text-2xl font-medium mt-4" style={{ color: "var(--text-primary)" }}>Set New Password</h2>
        </div>
        <div className="buddy-card p-8">
          {error && <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: "var(--error-bg)", color: "var(--error)" }}>{error}</div>}
          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <label className="label-caps block mb-1.5">New Password</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} className="buddy-input pr-10" placeholder="Min 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="reset-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }}>{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <div>
              <label className="label-caps block mb-1.5">Confirm Password</label>
              <input type="password" className="buddy-input" placeholder="Confirm your password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input" />
            </div>
            <button type="submit" className="btn-primary w-full py-3 mt-2" disabled={loading} data-testid="reset-submit-btn">
              {loading ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Reset Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
